import { Button, Col, DatePicker, Input, Row, Spin, Table, message } from "antd";
import { PageHeader } from "../../Common/MainLayout";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { DeleteOutlined, EditOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import BkGeneralSettingsAdminService from "../../Services/BkGeneralSettingsAdminService";

const GeneralSettingsBooks = () => {
    const [editable, setEditable] = useState<any>(false);
    const [loading, setLoading] = useState(false);

    const [standardRateData, setStandardRateData] = useState({});
    const [standardRates, setStandardRates] = useState<any[]>([]);
    const [advisaryRateData, setAdvisaryRateData] = useState<any[]>([]);
    const [advisoryTables, setAdvisoryTables] = useState<any[]>([]);

    const handleGetStandardRate = async () => {
        try {
            const res: any = await BkGeneralSettingsAdminService.getStandardRate();
            if (res?.result) {
                setStandardRateData(res.result);

                const mapped = Object.entries(res.result).map(([vehicle, values]: any, index) => ({
                    key: index,
                    vehicle,
                    upto: values?.belowThreshold?.rate || "",
                    above: values?.aboveThreshold?.rate || "",
                }));

                setStandardRates(mapped);
            }
        } catch (err) {
            console.error("Error fetching standard rates:", err);
        }
    };

    const handleGetAdvisaryRate = async () => {
        try {
            const res: any = await BkGeneralSettingsAdminService.getAllAdvisoryRate();
            if (res?.result) {
                setAdvisaryRateData(res.result);
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        handleGetAdvisaryRate();
        handleGetStandardRate();
    }, []);

    useEffect(() => {
        if (advisaryRateData && advisaryRateData.length > 0) {
            const mappedTables = advisaryRateData.map((item: any, index: number) => {
                const rows = item.nonDieselRates.map((nonDiesel: any, idx: number) => {
                    const dieselRate = item.dieselRates[idx]?.rate || 0;
                    const petrolRate = nonDiesel.rate?.petrol || 0;
                    const lpgRate = nonDiesel.rate?.lpg || 0;
                    const electricRate = nonDiesel.rate?.electric || 0;
                    const electricPublicRate = nonDiesel.rate?.electricPublic || 0;

                    return {
                        key: idx,
                        petrol: petrolRate,
                        diesel: dieselRate,
                        lpg: lpgRate,
                        home: electricRate,
                        public: electricPublicRate,
                    };
                });

                return {
                    id: Date.now() + index,
                    fromDate: item.duration?.from ? dayjs(item.duration.from) : null,
                    toDate: item.duration?.to ? dayjs(item.duration.to) : null,
                    data: JSON.parse(JSON.stringify(rows)),
                };
            });

            setAdvisoryTables(mappedTables);
        }
    }, [advisaryRateData]);

    const handleStandardChange = (key: number, field: string, value: any) => {
        setStandardRates((prev) =>
            prev.map((item) =>
                item.key === key ? { ...item, [field]: value } : item
            )
        );
    };

    // Updated handleDateChange with overlap & validation logic
    const handleDateChange = (id: any, field: "fromDate" | "toDate", date: any) => {
        setAdvisoryTables((prev: any) => {
            const updatedTables = [...prev];
            const currentIndex = updatedTables.findIndex((t) => t.id === id);
            if (currentIndex === -1) return prev;

            const currentTable = { ...updatedTables[currentIndex] };
            currentTable[field] = date;

            // Validate both dates
            if (currentTable.fromDate && currentTable.toDate) {
                const start = dayjs(currentTable.fromDate);
                const end = dayjs(currentTable.toDate);

                if (end.isBefore(start, "day")) {
                    message.error("End date must be after start date");
                    return prev;
                }

                // Check overlap with other tables
                const hasOverlap = updatedTables.some((t) => {
                    if (t.id === id || !t.fromDate || !t.toDate) return false;
                    const otherStart = dayjs(t.fromDate);
                    const otherEnd = dayjs(t.toDate);
                    return start.isSameOrBefore(otherEnd, "day") && end.isSameOrAfter(otherStart, "day");
                });

                if (hasOverlap) {
                    message.error("Date range overlaps with another advisory table.");
                    return prev;
                }
            }

            updatedTables[currentIndex] = currentTable;
            return updatedTables;
        });
    };

    const handleInputChange = (tableId: any, rowKey: number, field: string, value: any) => {
        setAdvisoryTables((prev) =>
            prev.map((table) =>
                table.id === tableId
                    ? {
                        ...table,
                        data: table.data.map((row: any) =>
                            row.key === rowKey ? { ...row, [field]: value } : row
                        ),
                    }
                    : table
            )
        );
    };

    // Prevent adding new table if existing ranges not completed
    const handleAddTable = () => {
        const hasUnfilled = advisoryTables.some(
            (t) => !t.fromDate || !t.toDate
        );

        if (hasUnfilled) {
            message.error("Please complete existing table date ranges before adding a new one.");
            return;
        }

        const newTable = {
            id: Date.now(),
            fromDate: null,
            toDate: null,
            data: [
                { key: 0, diesel: 0, petrol: 0, lpg: 0, home: 0, public: 0 },
                { key: 1, diesel: 0, petrol: 0, lpg: 0, home: 0, public: 0 },
                { key: 2, diesel: 0, petrol: 0, lpg: 0, home: 0, public: 0 },
            ],
        };

        setAdvisoryTables((prev) => [JSON.parse(JSON.stringify(newTable)), ...prev]);
    };

    const handleDelete = (id: any) => {
        setAdvisoryTables((prev) => prev.filter((t) => t.id !== id));
    };

    const getAdvisoryColumns: any = (tableId: any) => [
        {
            title: "Engine Size-all (except diesel)",
            render: (_: any, __: any, index: any) => (
                <Input
                    value={
                        index === 0
                            ? "1400cc or less"
                            : index === 1
                                ? "1401cc to 2000cc"
                                : "Over 2000cc"
                    }
                    disabled
                />
            ),
        },
        {
            title: "Engine Size-Diesel",
            render: (_: any, __: any, index: any) => (
                <Input
                    value={
                        index === 0
                            ? "1600cc or less"
                            : index === 1
                                ? "1601cc to 2000cc"
                                : "Over 2000cc"
                    }
                    disabled
                />
            ),
        },
        {
            title: "Petrol-rate per mile",
            dataIndex: "petrol",
            align: "right",
            render: (text: any, record: any) => (
                <Input
                    value={text}
                    disabled={!editable}
                    onChange={(e) => handleInputChange(tableId, record.key, "petrol", e.target.value)}
                />
            ),
        },
        {
            title: "Diesel-rate per mile",
            dataIndex: "diesel",
            align: "right",
            render: (text: any, record: any) => (
                <Input
                    value={text}
                    disabled={!editable}
                    onChange={(e) => handleInputChange(tableId, record.key, "diesel", e.target.value)}
                />
            ),
        },
        {
            title: "LPG-rate per mile",
            dataIndex: "lpg",
            align: "right",
            render: (text: any, record: any) => (
                <Input
                    value={text}
                    disabled={!editable}
                    onChange={(e) => handleInputChange(tableId, record.key, "lpg", e.target.value)}
                />
            ),
        },
        {
            title: "Electric-home-rate per mile",
            dataIndex: "home",
            align: "right",
            render: (text: any, record: any) => (
                <Input
                    value={text}
                    disabled={!editable}
                    onChange={(e) => handleInputChange(tableId, record.key, "home", e.target.value)}
                />
            ),
        },
        {
            title: "Electric-public-rate per mile",
            dataIndex: "public",
            align: "right",
            render: (text: any, record: any) => (
                <Input
                    value={text}
                    disabled={!editable}
                    onChange={(e) => handleInputChange(tableId, record.key, "public", e.target.value)}
                />
            ),
        },
    ];

    const handleSave = async () => {
        try {
            setLoading(true);

            for (const table of advisoryTables) {
                if (!table.fromDate || !table.toDate) {
                    message.error("Please select both From and To dates for all advisory rate tables.");
                    setLoading(false);
                    return;
                }
            }

            const standardRatesPayload = {
                carOrVan: {
                    aboveThreshold: { rate: Number(standardRates[0]?.above || 0) },
                    belowThreshold: { rate: Number(standardRates[0]?.upto || 0) },
                },
                motorCycle: {
                    aboveThreshold: { rate: Number(standardRates[1]?.above || 0) },
                    belowThreshold: { rate: Number(standardRates[1]?.upto || 0) },
                },
                biCycle: {
                    aboveThreshold: { rate: Number(standardRates[2]?.above || 0) },
                    belowThreshold: { rate: Number(standardRates[2]?.upto || 0) },
                },
                coPessangers: {
                    aboveThreshold: { rate: Number(standardRates[3]?.above || 0) },
                    belowThreshold: { rate: Number(standardRates[3]?.upto || 0) },
                },
            };

            const advisoryRatesPayload = advisoryTables.map((table: any) => ({
                duration: {
                    from: table.fromDate ? dayjs(table.fromDate).format("MM-DD-YYYY") : null,
                    to: table.toDate ? dayjs(table.toDate).format("MM-DD-YYYY") : null,
                },
                dieselRates: table.data.map((row: any) => ({
                    size: row.key,
                    rate: Number(row.diesel || 0),
                })),
                nonDieselRates: table.data.map((row: any) => ({
                    size: row.key,
                    rate: {
                        petrol: Number(row.petrol || 0),
                        lpg: Number(row.lpg || 0),
                        electric: Number(row.home || 0),
                        electricPublic: Number(row.public || 0),
                    },
                })),
            }));

            const payload = {
                standardRates: standardRatesPayload,
                advisoryRates: advisoryRatesPayload,
                recordStatus: 0,
                id: "string",
            };

            const res: any = await BkGeneralSettingsAdminService.postGeneralSettings(payload);

            if (res?.status) {
                setEditable(false);
                message.success(res?.message || "General settings saved successfully!");
            } else {
                if (res?.errors && res.errors.length > 0) {
                    res.errors.forEach((err: any) => {
                        message.error(err.message || "An error occurred while saving data.");
                    });
                } else {
                    message.error(res?.message || "Failed to save general settings!");
                }
            }
        } catch (error: any) {
            console.error("Save failed:", error);
            message.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Spin style={{ height: "100vh" }} spinning={(standardRates.length === 0 && advisaryRateData.length === 0) ? true : false} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>

            <Row justify="start" align="middle" style={{ margin: "1px 15px" }}>
                <PageHeader item={[{ label: "Books General Setting" }]} style={{ color: "#172B4D" }} />
            </Row>

            <div style={{ padding: 20, backgroundColor: "white" }}>
                <h3 style={{ marginBottom: 16 }}>Standard Flat Rates</h3>
                <Table
                    columns={[
                        {
                            title: "Vehicle Type",
                            dataIndex: "vehicle",
                            key: "vehicle",
                            render: (text: any) => <Input value={text} disabled />,
                        },
                        {
                            title: <div style={{ textAlign: "left" }}>Upto 10000 miles</div>,
                            dataIndex: "upto",
                            key: "upto",
                            align: "right",
                            render: (text: any, record: any) => (
                                <Input
                                    value={text}
                                    onChange={(e) => handleStandardChange(record.key, "upto", e.target.value)}
                                    disabled={!editable}
                                    style={{ textAlign: "right", width: "100%" }}
                                />
                            ),
                        },
                        {
                            title: <div style={{ textAlign: "left" }}>Above 10000 miles</div>,
                            dataIndex: "above",
                            key: "above",
                            align: "right",
                            render: (text: any, record: any) => (
                                <Input
                                    value={text}
                                    onChange={(e) => handleStandardChange(record.key, "above", e.target.value)}
                                    disabled={!editable}
                                    style={{ textAlign: "right", width: "100%" }}
                                />
                            ),
                        },
                    ]}
                    dataSource={standardRates}
                    pagination={false}
                    bordered
                    size="small"
                />

                <div style={{ marginTop: 32 }}>
                    <h3 style={{ marginBottom: 16 }}>Advisory Rates</h3>

                    <Button
                        type="default"
                        icon={<EditOutlined />}
                        style={{ marginBottom: 12 }}
                        onClick={() => setEditable(!editable)}
                    >
                        {editable ? "Stop Editing" : "Edit"}
                    </Button>
                    {editable && (
                        <>
                            <Button
                                type="primary"
                                style={{ marginLeft: 8, marginBottom: 12 }}
                                loading={loading}
                                onClick={handleSave}
                            >
                                Save
                            </Button>


                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                style={{ marginLeft: 8, marginBottom: 12 }}
                                onClick={handleAddTable}
                            >
                                Add
                            </Button>
                        </>
                    )}

                    {advisoryTables.map((table: any) => (
                        <div
                            key={table.id}
                            style={{
                                border: "1px solid #f0f0f0",
                                borderRadius: 8,
                                padding: 16,
                                marginBottom: 24,
                            }}
                        >
                            <Row gutter={16} style={{ marginBottom: 8 }}>
                                <Col>
                                    <DatePicker
                                        value={table.fromDate}
                                        disabled={!editable}
                                        format="DD/MM/YYYY"
                                        onChange={(date) => handleDateChange(table.id, "fromDate", date)}
                                    />
                                </Col>
                                <Col>
                                    <DatePicker
                                        value={table.toDate}
                                        disabled={!editable}
                                        format="DD/MM/YYYY"
                                        onChange={(date) => handleDateChange(table.id, "toDate", date)}
                                    />
                                </Col>
                                {editable && (
                                    <Col>
                                        <Button
                                            type="text"
                                            icon={<DeleteOutlined />}
                                            danger
                                            onClick={() => handleDelete(table.id)}
                                        />
                                    </Col>
                                )}
                            </Row>
                            <Table
                                columns={getAdvisoryColumns(table.id)}
                                dataSource={table.data}
                                pagination={false}
                                bordered
                                size="small"
                            />
                        </div>
                    ))}
                </div>
            </div>
        </Spin>
    );
};

export default GeneralSettingsBooks;
